import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Seo from "../components/Seo";
import StatusBadge, { statusDescription } from "../components/StatusBadge";
import RsvpForm from "../components/RsvpForm";
import { LoadingState, ErrorState } from "../components/StateBlocks";
import { formatEventDate, formatEventTimeRange } from "../utils/dateTime";
import api, { ApiError } from "../utils/api";

export default function EventDetails() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getEvent(eventId)
      .then((data) => {
        if (!cancelled) setEvent(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Unable to load this event.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (loading) return <LoadingState label="Loading event details\u2026" />;
  if (error) return <ErrorState message={error} />;
  if (!event) return null;

  const canRsvp = event.status === "CONFIRMED" || event.status === "FULL";
  const spotsLeft = Math.max(0, (event.capacity || 0) - (event.confirmedTeenCount || 0));

  return (
    <>
      <Seo title={event.title} description={event.description} />
      <StatusBadge status={event.status} />
      <h1>{event.title}</h1>
      <p>{formatEventDate(event.startAt)}</p>
      <p>{formatEventTimeRange(event.startAt, event.endAt)}</p>
      <p>{statusDescription(event.status)}</p>

      {event.status === "CANCELED" && (
        <div className="alert alert-error" role="alert">
          This event has been canceled.
        </div>
      )}

      <section className="card">
        <h2>Details</h2>
        <p>{event.description}</p>
        {event.roomInfo && (
          <p>
            <strong>Room:</strong> {event.roomInfo}
          </p>
        )}
        {event.gradeRange && (
          <p>
            <strong>Grade range:</strong> {event.gradeRange}
          </p>
        )}
        {event.organizerName && (
          <p>
            <strong>Organizer:</strong> {event.organizerName}
          </p>
        )}
        {event.arrivalInstructions && (
          <p>
            <strong>Arrival instructions:</strong> {event.arrivalInstructions}
          </p>
        )}
        {event.accessibilityContact && (
          <p>
            <strong>Accessibility:</strong> {event.accessibilityContact}
          </p>
        )}
        {(event.status === "CONFIRMED" || event.status === "FULL") && (
          <>
            <p>
              {event.confirmedTeenCount} of {event.capacity} teen spots reserved
            </p>
            <div className="capacity-meter" role="img" aria-label={`${event.confirmedTeenCount} of ${event.capacity} spots reserved`}>
              <div
                className="capacity-meter-fill"
                style={{ width: `${Math.min(100, (event.confirmedTeenCount / event.capacity) * 100)}%` }}
              />
            </div>
          </>
        )}
        {event.venueReminders && event.venueReminders.length > 0 && (
          <>
            <h3>Please remember</h3>
            <ul>
              {event.venueReminders.map((reminder) => (
                <li key={reminder}>{reminder}</li>
              ))}
            </ul>
          </>
        )}
      </section>

      {canRsvp && (
        <section className="card" aria-labelledby="rsvp-heading">
          <h2 id="rsvp-heading">{spotsLeft > 0 ? "RSVP for your teens" : "Join the waitlist"}</h2>
          {rsvpSubmitted ? (
            <div className="alert alert-success" role="status">
              Thanks! Check your email for a confirmation link to complete your RSVP.
            </div>
          ) : (
            <RsvpForm eventId={event.eventId} onSuccess={() => setRsvpSubmitted(true)} />
          )}
        </section>
      )}
    </>
  );
}
